import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GripVertical, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function QuickButtons() {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [buttons, setButtons] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    prompt_text: '',
    sort_order: 0,
  });

  useEffect(() => {
    fetchData();
  }, [agentId]);

  const fetchData = async () => {
    try {
      const [agentRes, buttonsRes] = await Promise.all([
        api.get(`/agents/${agentId}`),
        api.get(`/agents/${agentId}/buttons`),
      ]);
      setAgent(agentRes.data);
      setButtons(buttonsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      navigate('/dashboard');
    }
  };

  const openDialog = (button = null) => {
    if (button) {
      setEditingButton(button);
      setFormData({
        label: button.label,
        prompt_text: button.prompt_text,
        sort_order: button.sort_order,
      });
    } else {
      setEditingButton(null);
      setFormData({
        label: '',
        prompt_text: '',
        sort_order: buttons.length,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingButton) {
        await api.put(`/buttons/${editingButton.button_id}`, formData);
        toast.success('Button updated successfully!');
      } else {
        await api.post(`/agents/${agentId}/buttons`, formData);
        toast.success('Button created successfully!');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save button');
    }
  };

  const handleDelete = async (buttonId) => {
    if (!window.confirm('Delete this button?')) return;

    try {
      await api.delete(`/buttons/${buttonId}`);
      toast.success('Button deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete button');
    }
  };

  if (!agent) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="rounded-full"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-heading font-bold text-foreground" data-testid="buttons-title">Quick Buttons</h1>
                  <p className="text-sm text-muted-foreground">{agent.name}</p>
                </div>
              </div>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => openDialog()}
                  className="rounded-full shadow-lg shadow-primary/20"
                  data-testid="add-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Button
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle data-testid="dialog-title">{editingButton ? 'Edit Button' : 'Create Button'}</DialogTitle>
                  <DialogDescription>
                    Quick buttons help users start conversations with pre-defined prompts
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="label">Button Label *</Label>
                    <Input
                      id="label"
                      placeholder="e.g., Getting Started"
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      className="rounded-xl"
                      required
                      data-testid="button-label-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="prompt_text">Prompt Text *</Label>
                    <Textarea
                      id="prompt_text"
                      placeholder="What message should be sent when clicked?"
                      value={formData.prompt_text}
                      onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
                      className="min-h-24 rounded-xl"
                      required
                      data-testid="button-prompt-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sort_order">Sort Order</Label>
                    <Input
                      id="sort_order"
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                      className="rounded-xl"
                      data-testid="button-order-input"
                    />
                  </div>
                  <div className="flex space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      className="flex-1 rounded-full"
                      data-testid="dialog-cancel"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 rounded-full" data-testid="dialog-submit">
                      {editingButton ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {buttons.length === 0 ? (
          <Card className="border-dashed border-2 border-border" data-testid="empty-state">
            <CardContent className="py-16 text-center">
              <Zap className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">No quick buttons yet</h3>
              <p className="text-muted-foreground mb-6">Add buttons to help users start conversations quickly</p>
              <Button onClick={() => openDialog()} className="rounded-full" data-testid="empty-add-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Button
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="buttons-list">
            {buttons.map((button) => (
              <Card
                key={button.button_id}
                className="border-border shadow-sm hover:shadow-md transition-shadow"
                data-testid={`button-card-${button.button_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <GripVertical className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-heading font-semibold text-foreground mb-2" data-testid={`button-label-${button.button_id}`}>
                        {button.label}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2" data-testid={`button-prompt-${button.button_id}`}>
                        {button.prompt_text}
                      </p>
                      <div className="flex items-center space-x-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDialog(button)}
                          className="rounded-full"
                          data-testid={`edit-${button.button_id}`}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(button.button_id)}
                          className="rounded-full text-destructive hover:text-destructive"
                          data-testid={`delete-${button.button_id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        #{button.sort_order}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
